import { PropsWithChildren, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { StakingPoolActions } from '../../../actions/new-staking-action';
import Button from '../../../external/components/Button/Button';
import Icon from '../../../external/components/Icon/Icon';
import { Skeleton } from '../../../external/components/SkeletonLoading/Skeleton';
import Text from '../../../external/components/Text/Text';
import { State } from '../../../reducers';
import { StakingPool } from '../../../services/staking/staking.types';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { useStakingData } from '../hooks/useStakingData';

interface StakingCardProps {
  className?: string;
  poolId: string;
  showStaked: boolean;
  showSidebar: boolean;
  stakingPools: StakingPool[];
  setShowSideBar: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const StyledWrapper = styled.div`
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #221c32bf;
  position: relative;
  padding: 1rem;
  height: 100%;
  justify-content: space-between;

  @media screen and (min-width: 768px) {
    padding: 2rem 3rem;
  }
`;

const StyledGradient = styled.div<Pick<StakingPool, 'networkId'>>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30%;
  background: transparent;
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
`;

const StyledBadge = styled.div`
  border-radius: 24px;
  background: #23c581;
  color: #171616;
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 0.75rem;
  letter-spacing: 1.4px;
  font-size: 0.75rem;
  z-index: 1;
`;

const StyledContainer = styled.div`
  margin: 1rem 0;

  @media screen and (min-width: 768px) {
    margin: 2rem 0;
  }
`;

export function StakingCardPreview({
  className,
  children,

  showSidebar,
  showStaked,
  stakingPools,
  poolId,
  setShowSideBar,
  setLoading,
  ...props
}: PropsWithChildren<StakingCardProps>) {
  const { address: walletAddress, pools } = useSelector(({ account, StakingPools }: State) => ({
    address: account.address,
    pools: StakingPools as StakingPool[],
  }));

  const pool = pools.filter((pool) => pool.stakingContractAddress === poolId)[0];

  const dispatch = useDispatch();

  const { setSelectedPoolId } = bindActionCreators(StakingPoolActions, dispatch);

  const { networkId, stakingTokenSymbol } = pool;
  const {
    balance: stakedBalance,
    balanceUsd: stakedBalanceUsd,
    balanceLoading,
    aprLoading,
    getBalance,
    getEstimatedApr,
  } = useStakingData({
    pool,
  });

  const balance = pool.stakedBalance || stakedBalance;
  const balanceUsd = pool.stakedBalanceUsd !== '0' ? pool.stakedBalanceUsd : stakedBalanceUsd;

  const apr = pool.apr;

  const balanceEqualsZero = BigNumberHelper.isLessThanOrEqualToZero(balance);

  const initNaked = async () => {
    setLoading(true);
    if (walletAddress && !pool.stakedBalance && pool.stakedBalance !== '0') {
      await getBalance();
    }

    if (!apr) {
      await getEstimatedApr();
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!showSidebar) {
      if (pool.poolType === 'SINGLE') initNaked();
    }
  }, [stakingPools]);

  return (
    <>
      {showStaked && balanceEqualsZero ? null : (
        <StyledContainer className='col-xlg-3 col-lg-4 col-sm-6 col-xs-12' {...props}>
          <StyledWrapper className={className}>
            <>
              {aprLoading || balanceLoading ? (
                <>
                  <Skeleton variant='circle' height='50px' width='50px' dark={true} />
                  <Skeleton height='18px' width='60px' dark={true} />
                  <br />
                  <Skeleton height='40px' width='90px' dark={true} />
                  <Skeleton height='32px' width='80px' dark={true} />
                </>
              ) : (
                <>
                  {!balanceEqualsZero && <StyledBadge>STAKING</StyledBadge>}
                  {<StyledGradient networkId={networkId} />}
                  <>
                    <Icon.Contract
                      type={stakingTokenSymbol}
                      width={50}
                      style={{ zIndex: 1 }}
                    ></Icon.Contract>
                    <h1 style={{ margin: '0' }}>
                      <Text dark={true} as='span' size='xl'>
                        {stakingTokenSymbol}
                      </Text>
                    </h1>
                    <Text color='white' style={{ margin: '-0.25rem 0' }} size='xxl' as='span'>
                      {apr} %{' '}
                      <Text color='grey' as='span' size='md'>
                        APR
                      </Text>
                    </Text>
                    {!balanceEqualsZero && (
                      <div
                        className='flex direction-column align-center'
                        style={{ marginTop: '0.5rem' }}
                      >
                        <Text color='grey' as='span' size='md'>
                          Currently staked
                        </Text>
                        <div className='flex wrap align-center justify-center'>
                          <Text
                            dark={true}
                            style={{
                              margin: '.5rem 0 0.25rem 0',
                              textAlign: 'center',
                              display: 'inline',
                            }}
                          >
                            {BigNumberHelper.formatBigNumb(balance)}{' '}
                            <Text dark={true} as='span' weight={600}>
                              {stakingTokenSymbol}
                            </Text>
                          </Text>
                          <Text
                            color='green'
                            style={{
                              margin: '.5rem 0 0.25rem .25rem',
                              display: 'inline',
                            }}
                          >
                            (${parseFloat(balanceUsd).toLocaleString()})
                          </Text>
                        </div>
                      </div>
                    )}

                    <Button
                      dark={true}
                      variant='secondary'
                      onClick={() => {
                        setShowSideBar(true);
                        setSelectedPoolId(pool.stakingContractAddress);
                      }}
                    >
                      {!balanceEqualsZero ? 'View stake' : 'Start stake'}
                    </Button>
                  </>
                </>
              )}
            </>
          </StyledWrapper>
        </StyledContainer>
      )}
    </>
  );
}
